import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { config } from "../config";
import { AuthService } from "./auth-service";

interface AuthContextValue {
  service: AuthService;
  email: string | undefined;
  authed: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function AuthInner({ children }: { children: ReactNode }) {
  const service = useMemo(() => new AuthService(config.apiBase, config.adminDomain), []);
  const [authed, setAuthed] = useState(() => service.isAuthenticated());

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    ux_mode: "popup",
    hosted_domain: config.adminDomain, // restrict the account chooser to our Workspace
    redirect_uri: config.redirect,
    scope: "openid email profile",
    onSuccess: async (resp) => {
      await service.handleLoginSuccess(resp.code);
      setAuthed(service.isAuthenticated());
    },
  });

  const value: AuthContextValue = {
    service,
    email: service.email,
    authed,
    login: () => googleLogin(),
    logout: () => {
      service.logout();
      setAuthed(false);
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <GoogleOAuthProvider clientId={config.clientId}>
      <AuthInner>{children}</AuthInner>
    </GoogleOAuthProvider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
